import React, { useMemo } from 'react';
import { IRegionShort, IRelatedForRegion } from '@/api/region/types';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import { RegionHeader } from '@/components/region/RegionHeader';
import type { SubNavItem } from '@/components/common/SubNav';
import { IResortPathInfo } from '@/api/resort/types';
import { getLocale } from '@/i18n/locale';
import { ELocale } from '@/i18n/localeEnum';
import {
  CurrentLink, CurrentTab, linksPlan, linksSnow,
} from './constants';

// Bridges the existing region _layout API (which still passes title /
// photo / related from the legacy HeroNav era) onto the v3
// RegionHeader. Link-building rules (linksSnow + linksPlan +
// FR Lodging exclusion for region 15) carry over unchanged; only the
// inner render swaps. The unused HeroNav-only props (photo, related,
// resort) stay in the signature so _layout.tsx doesn't have to change
// in the same PR.
export const RegionHeaderNav = ({
  region,
  link,
  title,
}: {
  region: IRegionShort,
  title: string,
  photo: string,
  tab: CurrentTab,
  link: CurrentLink,
  related: IRelatedForRegion,
  resort: IResortPathInfo
}) => {
  const { t } = useOTSTranslation('common');
  const locale = getLocale() as ELocale;

  const linksSnowPart = useMemo(
    () => linksSnow(region, t),
    [locale, region, t],
  );
  const linksPlanningPart = useMemo(
    () => linksPlan(region, t),
    [region, t],
  );

  // Map both link groups into the v3 SubNav shape; mark the active
  // item so RegionHeader can paint the underline. Planning
  // links render first (Overview), matching the legacy order.
  const subNavItems: SubNavItem[] = useMemo(
    () => [...linksPlanningPart, ...linksSnowPart].map((l) => ({
      label: l.title,
      href: l.href,
      current: l.uuid === link,
      isExternal: l.isExternal,
    })),
    [linksPlanningPart, linksSnowPart, link],
  );

  // The current item's label doubles as the H1 page suffix
  // (e.g. "Colorado Snow Report").

  return (
    <RegionHeader
      country={region.country}
      regionName={region.title}
      heading={title}
      subNav={subNavItems}
      className="mt-4"
    />
  );
};
